import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import './LeadMagnetFormModal.css';
import { formatFileSize } from './mockData';
import { Modal, Button, TextInput, Textarea, Select, Alert } from '../../common';
import { leadMagnetApi } from '../../../services/apis/configuredApis';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';

// Each slot is an internal authoring beat (Problem/Enemy/Stakes/Insight/
// Solution/Proof/Action). Those names are NEVER shown to readers -- only the
// admin sees them as a hint while filling the form. The customer-facing
// title is the optional Section Title field per slot.
const SOUND_BYTE_FIELDS = [
  {
    key: 'problem',
    hint: 'Problem (internal)',
    titlePlaceholder: 'Optional section title (e.g. "Why most enterprise AI fails")',
    bodyPlaceholder: 'Write the painful truth in one sentence.\nE.g. "Enterprise AI looks impressive in demos, but breaks when accuracy, speed, cost, and auditability matter."',
  },
  {
    key: 'enemy',
    hint: 'Enemy (internal)',
    titlePlaceholder: 'Optional section title (e.g. "The LLM + RAG ceiling")',
    bodyPlaceholder: 'Name what is failing.\nE.g. "The current LLM + RAG stack was not designed for deterministic enterprise decisioning."',
  },
  {
    key: 'stakes',
    hint: 'Stakes (internal)',
    titlePlaceholder: 'Optional section title (e.g. "Why this matters now")',
    bodyPlaceholder: 'Explain why this matters now.\nE.g. "When AI gives inconsistent answers, leaders cannot scale it, auditors cannot trust it..."',
  },
  {
    key: 'insight',
    hint: 'Insight (internal)',
    titlePlaceholder: 'Optional section title (e.g. "The shift no one is talking about")',
    bodyPlaceholder: 'Reveal the core shift.\nE.g. "The future of enterprise AI is not bigger prompts; it is controlled reasoning over enterprise knowledge."',
  },
  {
    key: 'solution',
    hint: 'Solution (internal)',
    titlePlaceholder: 'Optional section title (e.g. "What CerebralMachines does")',
    bodyPlaceholder: 'Say what CM does simply.\nE.g. "CerebralMachines makes enterprise AI reliable, fast, auditable, and affordable."',
  },
  {
    key: 'proof',
    hint: 'Proof (internal)',
    titlePlaceholder: 'Optional section title (e.g. "The numbers")',
    bodyPlaceholder: 'Use numbers.\nE.g. "Cut AI token costs by up to 70% while delivering accurate, consistent, auditable AI decisions in under 5 seconds."',
  },
  {
    key: 'action',
    hint: 'Action (internal)',
    titlePlaceholder: 'Optional section title (e.g. "What to do next")',
    bodyPlaceholder: 'Tell them what to do next.\nE.g. "If your AI initiative is stuck, let\'s identify the workflow where CerebralMachines can prove the difference in 6 weeks."',
    hasInlineLink: true,
    linkCaptionPlaceholder: 'e.g. Book a 30-minute conversation',
    linkUrlPlaceholder: 'https://cerebralmachines.ai/contact',
  },
];

const DEFAULT_FORM_VALUES = {
  internalName: '',
  slug: '',
  status: 'draft',
  pageType: 'STANDARD',
  pageTitle: '',
  heroHeadline: '',
  heroSubHeadline: '',
  bulletPoints: [{ value: '' }],
  credibilityText: '',
  formCtaText: 'Download Now',
  seoTitle: '',
  seoDescription: '',
  assetId: '',
  soundBytes: {
    problem: { title: '', body: '' },
    enemy: { title: '', body: '' },
    stakes: { title: '', body: '' },
    insight: { title: '', body: '' },
    solution: { title: '', body: '' },
    proof: { title: '', body: '' },
    action: { title: '', body: '', link: { caption: '', url: '' } },
  },
  funnelLinks: [],
  ctaBox: {
    type: 'VIDEO',
    title: '',
    description: '',
    button_text: '',
    video_url: '',
    external_url: '',
    asset_id: '',
  },
};

function LeadMagnetFormModal({ show, leadMagnet, assets, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isCerebralAdmin = currentUserHasCerebralAdminRole();
  
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setError: setFormError,
    clearErrors,
    watch,
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const bulletPoints = watch('bulletPoints');
  const pageType = watch('pageType');
  const ctaType = watch('ctaBox.type');
  const isStorytelling = pageType === 'STORYTELLING';

  // Clear bullet points error when bullet points change
  useEffect(() => {
    const hasNonEmptyBullet = bulletPoints?.some(bp => bp.value?.trim());
    if (hasNonEmptyBullet && errors.bulletPoints) {
      clearErrors('bulletPoints');
    }
  }, [bulletPoints, errors.bulletPoints, clearErrors]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bulletPoints',
  });

  const {
    fields: funnelFields,
    append: appendFunnel,
    remove: removeFunnel,
  } = useFieldArray({
    control,
    name: 'funnelLinks',
  });

  useEffect(() => {
    if (!show) {
      setError(null);
      return;
    }
    
    if (leadMagnet) {
      // Convert status from uppercase (API) to lowercase (form)
      const statusValue = leadMagnet.status ?? DEFAULT_FORM_VALUES.status;
      const normalizedStatus = statusValue ? statusValue.toLowerCase() : DEFAULT_FORM_VALUES.status;
      const pageTypeValue = (leadMagnet.pageType || leadMagnet.page_type || 'STANDARD').toUpperCase();

      // Normalise both legacy strings and the new {title, body[, link]} shape
      // so the form always works against object slots. The link sub-object is
      // only populated for slots flagged with hasInlineLink (currently just
      // Action).
      const apiSoundBytes = leadMagnet.soundBytes || leadMagnet.sound_bytes || {};
      const soundBytesValues = SOUND_BYTE_FIELDS.reduce((acc, field) => {
        const raw = apiSoundBytes[field.key];
        let slotValue;
        if (typeof raw === 'string') {
          slotValue = { title: '', body: raw };
        } else if (raw && typeof raw === 'object') {
          slotValue = {
            title: raw.title || '',
            body: raw.body || '',
          };
        } else {
          slotValue = { title: '', body: '' };
        }
        if (field.hasInlineLink) {
          const rawLink = (raw && typeof raw === 'object' && raw.link && typeof raw.link === 'object')
            ? raw.link
            : {};
          slotValue.link = {
            caption: rawLink.caption || '',
            url: rawLink.url || '',
          };
        }
        acc[field.key] = slotValue;
        return acc;
      }, {});

      const apiFunnelLinks = leadMagnet.funnelLinks || leadMagnet.funnel_links || [];
      const funnelLinksValues = Array.isArray(apiFunnelLinks)
        ? apiFunnelLinks.map(l => ({ text: l.text || '', url: l.url || '' }))
        : [];

      const apiCtaBox = leadMagnet.ctaBox || leadMagnet.cta_box || {};
      const ctaBoxValues = {
        type: apiCtaBox.type || 'VIDEO',
        title: apiCtaBox.title || '',
        description: apiCtaBox.description || '',
        button_text: apiCtaBox.button_text || '',
        video_url: apiCtaBox.video_url || '',
        external_url: apiCtaBox.external_url || '',
        asset_id: apiCtaBox.asset_id || '',
      };
      
      reset({
        ...DEFAULT_FORM_VALUES,
        internalName: leadMagnet.internalName ?? leadMagnet.internal_name ?? DEFAULT_FORM_VALUES.internalName,
        slug: leadMagnet.slug ?? DEFAULT_FORM_VALUES.slug,
        status: normalizedStatus,
        pageType: pageTypeValue === 'STORYTELLING' ? 'STORYTELLING' : 'STANDARD',
        pageTitle: leadMagnet.pageTitle ?? leadMagnet.page_title ?? DEFAULT_FORM_VALUES.pageTitle,
        heroHeadline: leadMagnet.heroHeadline ?? leadMagnet.hero_headline ?? DEFAULT_FORM_VALUES.heroHeadline,
        heroSubHeadline: leadMagnet.heroSubHeadline ?? leadMagnet.hero_subheadline ?? DEFAULT_FORM_VALUES.heroSubHeadline,
        bulletPoints: (leadMagnet.bulletPoints || leadMagnet.bullet_points) && (leadMagnet.bulletPoints || leadMagnet.bullet_points).length > 0
          ? (leadMagnet.bulletPoints || leadMagnet.bullet_points).map(bp => ({ value: bp }))
          : DEFAULT_FORM_VALUES.bulletPoints,
        credibilityText: leadMagnet.credibilityText ?? leadMagnet.credibility_text ?? DEFAULT_FORM_VALUES.credibilityText,
        formCtaText: leadMagnet.formCtaText ?? leadMagnet.cta_text ?? DEFAULT_FORM_VALUES.formCtaText,
        seoTitle: leadMagnet.seoTitle ?? leadMagnet.seo_title ?? DEFAULT_FORM_VALUES.seoTitle,
        seoDescription: leadMagnet.seoDescription ?? leadMagnet.seo_description ?? DEFAULT_FORM_VALUES.seoDescription,
        assetId: leadMagnet.assetId ?? leadMagnet.asset_id ?? DEFAULT_FORM_VALUES.assetId,
        soundBytes: soundBytesValues,
        funnelLinks: funnelLinksValues,
        ctaBox: ctaBoxValues,
      });
    } else {
      // Reset form for new lead magnet
      reset(DEFAULT_FORM_VALUES);
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, leadMagnet?.id || leadMagnet?.lead_magnet_id]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const isStory = data.pageType === 'STORYTELLING';

      // Filter out empty bullet points
      const cleanedBulletPoints = data.bulletPoints
        .map(bp => bp.value?.trim())
        .filter(bp => bp);

      // Standard pages still require at least one bullet point. Storytelling
      // pages don't use bullet points so we skip the requirement.
      if (!isStory && cleanedBulletPoints.length === 0) {
        setFormError('bulletPoints', {
          type: 'manual',
          message: 'At least one bullet point is required',
        });
        setLoading(false);
        return;
      }

      // Build sound_bytes object (only for storytelling). Each slot is the
      // new {title, body} shape; slots whose body is empty are dropped so
      // storage stays compact and the public template doesn't render an
      // empty section.
      // Pre-pass: for slots that allow an inline link (currently Action only),
      // require both caption and URL together. Half-filled link is a user
      // error we surface before building the payload.
      let inlineLinkError = false;
      SOUND_BYTE_FIELDS.forEach((field) => {
        if (!field.hasInlineLink) return;
        const rawLink = data.soundBytes?.[field.key]?.link || {};
        const caption = (rawLink.caption || '').trim();
        const url = (rawLink.url || '').trim();
        if ((caption && !url) || (!caption && url)) {
          setFormError(`soundBytes.${field.key}.link.url`, {
            type: 'manual',
            message: 'Provide both a caption and a URL, or leave both blank',
          });
          inlineLinkError = true;
        }
      });
      if (inlineLinkError) {
        setLoading(false);
        return;
      }

      const cleanedSoundBytes = SOUND_BYTE_FIELDS.reduce((acc, field) => {
        const raw = data.soundBytes?.[field.key] || {};
        const body = (raw.body || '').trim();
        const title = (raw.title || '').trim();
        if (!body) return acc;
        const slot = { title: title || null, body };
        if (field.hasInlineLink) {
          const caption = (raw.link?.caption || '').trim();
          const url = (raw.link?.url || '').trim();
          if (caption && url) {
            slot.link = { caption, url };
          }
        }
        acc[field.key] = slot;
        return acc;
      }, {});

      // Storytelling pages must have at least one sound byte (with a body)
      if (isStory && Object.keys(cleanedSoundBytes).length === 0) {
        setFormError('soundBytes.problem.body', {
          type: 'manual',
          message: 'At least one sound byte is required for a storytelling page',
        });
        setLoading(false);
        return;
      }

      // Build funnel_links list (drop empty rows)
      const cleanedFunnelLinks = (data.funnelLinks || [])
        .map(l => ({ text: (l.text || '').trim(), url: (l.url || '').trim() }))
        .filter(l => l.text && l.url);

      // Build cta_box object (only for storytelling)
      let cleanedCtaBox = null;
      if (isStory && data.ctaBox && data.ctaBox.type) {
        const cb = data.ctaBox;
        cleanedCtaBox = {
          type: cb.type,
          title: cb.title?.trim() || null,
          description: cb.description?.trim() || null,
          button_text: cb.button_text?.trim() || null,
          video_url: cb.type === 'VIDEO' ? (cb.video_url?.trim() || null) : null,
          external_url: cb.type === 'LINK' ? (cb.external_url?.trim() || null) : null,
          asset_id: cb.type === 'DOWNLOAD' ? (cb.asset_id || null) : null,
        };

        // Validate variant-required fields
        if (cb.type === 'VIDEO' && !cleanedCtaBox.video_url) {
          setFormError('ctaBox.video_url', { type: 'manual', message: 'Video URL is required' });
          setLoading(false);
          return;
        }
        if (cb.type === 'LINK' && !cleanedCtaBox.external_url) {
          setFormError('ctaBox.external_url', { type: 'manual', message: 'Link URL is required' });
          setLoading(false);
          return;
        }
        if (cb.type === 'DOWNLOAD' && !cleanedCtaBox.asset_id) {
          setFormError('ctaBox.asset_id', { type: 'manual', message: 'Please select a downloadable asset' });
          setLoading(false);
          return;
        }
      }

      // Transform camelCase to snake_case for API and uppercase status.
      // Slug is sent only when explicitly provided so the backend's
      // auto-slug-generation on publish still kicks in for blank values.
      const trimmedSlug = (data.slug || '').trim();
      const apiData = {
        internal_name: data.internalName,
        slug: trimmedSlug || null,
        status: data.status?.toUpperCase() || 'DRAFT',
        page_title: data.pageTitle,
        hero_headline: data.heroHeadline,
        hero_subheadline: data.heroSubHeadline || null,
        bullet_points: cleanedBulletPoints,
        credibility_text: data.credibilityText || null,
        cta_text: data.formCtaText,
        seo_title: data.seoTitle || null,
        seo_description: data.seoDescription || null,
        asset_id: data.assetId || null,
        page_type: isStory ? 'STORYTELLING' : 'STANDARD',
        sound_bytes: isStory ? cleanedSoundBytes : {},
        funnel_links: isStory ? cleanedFunnelLinks : [],
        cta_box: isStory ? (cleanedCtaBox || {}) : {},
      };

      if (leadMagnet) {
        // Update existing lead magnet
        await leadMagnetApi.leadMagnetUpdateLeadMagnetIdPut({
          leadMagnetId: leadMagnet.id || leadMagnet.lead_magnet_id,
          leadMagnetUpdate: apiData,
        });
      } else {
        // Create new lead magnet
        await leadMagnetApi.leadMagnetCreatePost({
          leadMagnetCreate: apiData,
        });
      }
      
      // Close modal and notify parent of success
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error saving lead magnet:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save lead magnet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = leadMagnet ? 'Edit Lead Magnet' : 'Create Lead Magnet';

  const footer = (
    <div className="lead-magnet-form-modal__footer">
      <Button
        variant="secondary"
        onClick={onClose}
        type="button"
        disabled={loading}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit(onSubmit)}
        type="button"
        disabled={loading}
      >
        {loading ? 'Saving...' : (leadMagnet ? 'Update' : 'Create')} Lead Magnet
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      title={modalTitle}
      footer={footer}
    >
      {error && (
        <Alert
          variant="error"
          onClose={() => setError(null)}
          className="lead-magnet-form-modal__alert"
        >
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="">
            {/* Internal Name */}
            <div className="lead-magnet-form-modal__field">
              <TextInput
                id="internalName"
                label={
                  <>
                    Internal Name <span className="lead-magnet-form-modal__required">*</span>
                  </>
                }
                variant="modal"
                {...register('internalName', {
                  required: 'Internal name is required',
                })}
              />
              {errors.internalName && (
                <span className="lead-magnet-form-modal__error">{errors.internalName.message}</span>
              )}
            </div>

            {/* URL Slug */}
            <div className="lead-magnet-form-modal__field">
              <TextInput
                id="slug"
                label="URL Slug"
                placeholder="ai-in-healthcare-is-falling-short"
                hint="Lowercase letters, numbers, and hyphens. The page lives at /lm/<slug>. Auto-generated from the page title if left blank when publishing."
                variant="modal"
                {...register('slug', {
                  pattern: {
                    value: /^[a-z0-9-]*$/,
                    message: 'Use lowercase letters, numbers, and hyphens only',
                  },
                })}
              />
              {errors.slug && (
                <span className="lead-magnet-form-modal__error">{errors.slug.message}</span>
              )}
            </div>

            {/* Page Type */}
            <div className="lead-magnet-form-modal__field">
              <Controller
                name="pageType"
                control={control}
                render={({ field }) => (
                  <Select
                    id="pageType"
                    label="Page Type"
                    variant="modal"
                    {...field}
                    placeholder=""
                    options={[
                      { value: 'STANDARD', label: 'Standard (form + download)' },
                      { value: 'STORYTELLING', label: 'Storytelling (sound bytes + CTA)' },
                    ]}
                    hint="Standard captures email/contact info before unlocking a download. Storytelling is a content-first page with a single right-side CTA (video, download, or link)."
                  />
                )}
              />
            </div>

            {/* Status - Only CerebralAdmin can change to/from Published */}
            <div className="lead-magnet-form-modal__field">
              <Controller
                name="status"
                control={control}
                render={({ field }) => {
                  const statusOptions = isCerebralAdmin
                    ? [
                        { value: 'draft', label: 'Draft' },
                        { value: 'published', label: 'Published' },
                        { value: 'archived', label: 'Archived' },
                      ]
                    : [
                        { value: 'draft', label: 'Draft' },
                        { value: 'archived', label: 'Archived' },
                      ];
                  
                  return (
                    <Select
                      id="status"
                      label="Status"
                      variant="modal"
                      {...field}
                      options={statusOptions}
                      disabled={!isCerebralAdmin && (field.value === 'published' || leadMagnet?.status?.toLowerCase() === 'published')}
                      hint={!isCerebralAdmin ? 'Only CerebralAdmin can publish/unpublish lead magnets' : undefined}
                    />
                  );
                }}
              />
            </div>

            {/* Page Title */}
            <div className="lead-magnet-form-modal__field">
              <TextInput
                id="pageTitle"
                label={
                  <>
                    Page Title <span className="lead-magnet-form-modal__required">*</span>
                  </>
                }
                variant="modal"
                {...register('pageTitle', {
                  required: 'Page title is required',
                })}
              />
              {errors.pageTitle && (
                <span className="lead-magnet-form-modal__error">{errors.pageTitle.message}</span>
              )}
            </div>

            {/* Hero Headline */}
            <div className="lead-magnet-form-modal__field">
              <TextInput
                id="heroHeadline"
                label={
                  <>
                    Hero Headline <span className="lead-magnet-form-modal__required">*</span>
                  </>
                }
                variant="modal"
                {...register('heroHeadline', {
                  required: 'Hero headline is required',
                })}
              />
              {errors.heroHeadline && (
                <span className="lead-magnet-form-modal__error">{errors.heroHeadline.message}</span>
              )}
            </div>

            {/* Hero Sub-headline (required for STANDARD, optional for STORYTELLING) */}
            <div className="lead-magnet-form-modal__field">
              <TextInput
                id="heroSubHeadline"
                label={
                  <>
                    Hero Sub-headline {!isStorytelling && <span className="lead-magnet-form-modal__required">*</span>}
                  </>
                }
                variant="modal"
                {...register('heroSubHeadline', {
                  required: isStorytelling ? false : 'Hero sub-headline is required',
                })}
              />
              {errors.heroSubHeadline && (
                <span className="lead-magnet-form-modal__error">{errors.heroSubHeadline.message}</span>
              )}
            </div>

            {/* STANDARD-only fields */}
            {!isStorytelling && (
              <>
                {/* Bullet Points */}
                <div className="lead-magnet-form-modal__field">
                  <label>
                    Bullet Points <span className="lead-magnet-form-modal__required">*</span>
                  </label>
                  {fields.map((field, index) => (
                    <div key={field.id} className="lead-magnet-form-modal__bullet-row">
                      <Controller
                        name={`bulletPoints.${index}.value`}
                        control={control}
                        render={({ field: bulletField }) => (
                          <TextInput
                            {...bulletField}
                            placeholder={`Bullet point ${index + 1}`}
                            variant="modal"
                          />
                        )}
                      />
                      {fields.length > 1 && (
                        <Button
                          variant="ghost"
                          onClick={() => remove(index)}
                          type="button"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    onClick={() => append({ value: '' })}
                    type="button"
                  >
                    + Add Bullet Point
                  </Button>
                  {errors.bulletPoints && (
                    <span className="lead-magnet-form-modal__error">{errors.bulletPoints.message}</span>
                  )}
                </div>

                {/* Credibility Text */}
                <div className="lead-magnet-form-modal__field">
                  <TextInput
                    id="credibilityText"
                    label="Credibility / Trust Text"
                    placeholder="e.g., Trusted by 10,000+ users"
                    variant="modal"
                    {...register('credibilityText')}
                  />
                </div>

                {/* Form CTA Button Text */}
                <div className="lead-magnet-form-modal__field">
                  <TextInput
                    id="formCtaText"
                    label={
                      <>
                        Form CTA Button Text <span className="lead-magnet-form-modal__required">*</span>
                      </>
                    }
                    variant="modal"
                    {...register('formCtaText', {
                      required: 'Form CTA button text is required',
                    })}
                  />
                  {errors.formCtaText && (
                    <span className="lead-magnet-form-modal__error">{errors.formCtaText.message}</span>
                  )}
                </div>

                {/* Attach Asset (download for the standard form) */}
                <div className="lead-magnet-form-modal__field">
                  <Controller
                    name="assetId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        id="assetId"
                        label="Attach Asset (Optional)"
                        variant="modal"
                        placeholder="None"
                        {...field}
                        options={assets.map((asset) => ({
                          value: asset.id,
                          label: `${asset.fileName} (${formatFileSize(asset.fileSize)})`,
                        }))}
                        hint="Select an uploaded asset or upload a new one from the Assets tab"
                      />
                    )}
                  />
                </div>
              </>
            )}

            {/* STORYTELLING-only fields */}
            {isStorytelling && (
              <>
                <div className="lead-magnet-form-modal__section">
                  <h3 className="lead-magnet-form-modal__section-title">Sound Bytes</h3>
                  <p className="lead-magnet-form-modal__section-help">
                    Seven authoring beats that drive your story. Each beat has an optional Section Title (shown to readers) and a Body. Leave a body blank to hide that beat on the page. The internal beat name (Problem, Enemy, etc.) is never shown to readers.
                  </p>
                  {SOUND_BYTE_FIELDS.map((sb) => (
                    <div key={sb.key} className="lead-magnet-form-modal__field lead-magnet-form-modal__sb-block">
                      <span className="lead-magnet-form-modal__sb-hint">{sb.hint}</span>
                      <TextInput
                        id={`soundBytes-${sb.key}-title`}
                        label="Section Title (optional)"
                        placeholder={sb.titlePlaceholder}
                        variant="modal"
                        {...register(`soundBytes.${sb.key}.title`)}
                      />
                      <Textarea
                        id={`soundBytes-${sb.key}-body`}
                        label="Body"
                        rows={3}
                        placeholder={sb.bodyPlaceholder}
                        variant="modal"
                        {...register(`soundBytes.${sb.key}.body`)}
                      />
                      {errors.soundBytes?.[sb.key]?.body && (
                        <span className="lead-magnet-form-modal__error">
                          {errors.soundBytes[sb.key].body.message}
                        </span>
                      )}
                      {sb.hasInlineLink && (
                        <div className="lead-magnet-form-modal__sb-link">
                          <p className="lead-magnet-form-modal__sb-link-help">
                            Optional inline call-to-action link rendered at the end of this section. Leave both fields blank to omit.
                          </p>
                          <TextInput
                            id={`soundBytes-${sb.key}-link-caption`}
                            label="Link Caption"
                            placeholder={sb.linkCaptionPlaceholder}
                            variant="modal"
                            {...register(`soundBytes.${sb.key}.link.caption`)}
                          />
                          <TextInput
                            id={`soundBytes-${sb.key}-link-url`}
                            label="Link URL"
                            placeholder={sb.linkUrlPlaceholder}
                            variant="modal"
                            {...register(`soundBytes.${sb.key}.link.url`)}
                          />
                          {errors.soundBytes?.[sb.key]?.link?.url && (
                            <span className="lead-magnet-form-modal__error">
                              {errors.soundBytes[sb.key].link.url.message}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="lead-magnet-form-modal__section">
                  <h3 className="lead-magnet-form-modal__section-title">Additional Links</h3>
                  <p className="lead-magnet-form-modal__section-help">
                    Optional follow-up links that draw visitors deeper into the funnel.
                  </p>
                  {funnelFields.map((field, index) => (
                    <div key={field.id} className="lead-magnet-form-modal__funnel-row">
                      <div className="lead-magnet-form-modal__funnel-row-fields">
                        <Controller
                          name={`funnelLinks.${index}.text`}
                          control={control}
                          render={({ field: f }) => (
                            <TextInput
                              {...f}
                              placeholder="Link text"
                              variant="modal"
                            />
                          )}
                        />
                        <Controller
                          name={`funnelLinks.${index}.url`}
                          control={control}
                          render={({ field: f }) => (
                            <TextInput
                              {...f}
                              placeholder="https://..."
                              variant="modal"
                            />
                          )}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        onClick={() => removeFunnel(index)}
                        type="button"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    onClick={() => appendFunnel({ text: '', url: '' })}
                    type="button"
                  >
                    + Add Link
                  </Button>
                </div>

                <div className="lead-magnet-form-modal__section">
                  <h3 className="lead-magnet-form-modal__section-title">Right-side CTA Box</h3>
                  <p className="lead-magnet-form-modal__section-help">
                    The single action you want every visitor to take.
                  </p>

                  <div className="lead-magnet-form-modal__field">
                    <Controller
                      name="ctaBox.type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          id="ctaBox-type"
                          label="CTA Type"
                          variant="modal"
                          placeholder=""
                          {...field}
                          options={[
                            { value: 'VIDEO', label: 'Video (YouTube / Vimeo embed)' },
                            { value: 'DOWNLOAD', label: 'Download (attached asset)' },
                            { value: 'LINK', label: 'External link' },
                          ]}
                        />
                      )}
                    />
                  </div>

                  <div className="lead-magnet-form-modal__field">
                    <TextInput
                      id="ctaBox-title"
                      label="CTA Title"
                      placeholder="e.g., Watch the demo"
                      variant="modal"
                      {...register('ctaBox.title')}
                    />
                  </div>

                  <div className="lead-magnet-form-modal__field">
                    <Textarea
                      id="ctaBox-description"
                      label="CTA Description"
                      rows={2}
                      placeholder="Short supporting copy below the title."
                      variant="modal"
                      {...register('ctaBox.description')}
                    />
                  </div>

                  {ctaType !== 'VIDEO' && (
                    <div className="lead-magnet-form-modal__field">
                      <TextInput
                        id="ctaBox-button-text"
                        label="Button Text"
                        placeholder="e.g., Download Now / Book a Call"
                        variant="modal"
                        {...register('ctaBox.button_text')}
                      />
                    </div>
                  )}

                  {ctaType === 'VIDEO' && (
                    <div className="lead-magnet-form-modal__field">
                      <TextInput
                        id="ctaBox-video-url"
                        label={
                          <>
                            Video URL <span className="lead-magnet-form-modal__required">*</span>
                          </>
                        }
                        placeholder="https://www.youtube.com/watch?v=..."
                        variant="modal"
                        {...register('ctaBox.video_url')}
                      />
                      {errors.ctaBox?.video_url && (
                        <span className="lead-magnet-form-modal__error">{errors.ctaBox.video_url.message}</span>
                      )}
                    </div>
                  )}

                  {ctaType === 'LINK' && (
                    <div className="lead-magnet-form-modal__field">
                      <TextInput
                        id="ctaBox-external-url"
                        label={
                          <>
                            Link URL <span className="lead-magnet-form-modal__required">*</span>
                          </>
                        }
                        placeholder="https://..."
                        variant="modal"
                        {...register('ctaBox.external_url')}
                      />
                      {errors.ctaBox?.external_url && (
                        <span className="lead-magnet-form-modal__error">{errors.ctaBox.external_url.message}</span>
                      )}
                    </div>
                  )}

                  {ctaType === 'DOWNLOAD' && (
                    <div className="lead-magnet-form-modal__field">
                      <Controller
                        name="ctaBox.asset_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            id="ctaBox-asset-id"
                            label={
                              <>
                                Downloadable Asset <span className="lead-magnet-form-modal__required">*</span>
                              </>
                            }
                            variant="modal"
                            placeholder="None"
                            {...field}
                            options={assets.map((asset) => ({
                              value: asset.id,
                              label: `${asset.fileName} (${formatFileSize(asset.fileSize)})`,
                            }))}
                          />
                        )}
                      />
                      {errors.ctaBox?.asset_id && (
                        <span className="lead-magnet-form-modal__error">{errors.ctaBox.asset_id.message}</span>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* SEO Title */}
            <div className="lead-magnet-form-modal__field">
              <TextInput
                id="seoTitle"
                label="SEO Title (Optional)"
                placeholder="For meta tags"
                variant="modal"
                {...register('seoTitle')}
              />
            </div>

            {/* SEO Description */}
            <div className="lead-magnet-form-modal__field">
              <Textarea
                id="seoDescription"
                rows={3}
                label="SEO Description (Optional)"
                placeholder="For meta tags"
                variant="modal"
                {...register('seoDescription')}
              />
            </div>
        </div>
      </form>
    </Modal>
  );
}

export default LeadMagnetFormModal;
